import React, { useState } from "react";
import FreqLoader from "@/components/FreqLoader";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { ShieldAlert, ArrowLeft } from "lucide-react";

/**
 * Floating banner rendered globally when an admin is impersonating a user.
 * Reads `user.impersonated_by` on the auth context and offers a one-click
 * "Return to admin" that hits POST /api/auth/stop-impersonation.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user?.impersonated_by) return null;

  const stop = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/stop-impersonation");
      toast.success("Returned to admin account");
      window.location.href = "/admin";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not restore admin session");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed top-0 inset-x-0 z-[100] bg-gradient-to-r from-amber-500/90 via-amber-500/95 to-orange-500/90 text-black shadow-lg backdrop-blur-sm"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <ShieldAlert size={16} className="shrink-0" />
          <span>
            <strong>Admin view:</strong> You are logged in as{" "}
            <strong>{user.name || user.email}</strong> — all actions are logged.
          </span>
        </div>
        <button
          onClick={stop}
          disabled={busy}
          className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider bg-black/80 hover:bg-black text-white rounded-full px-3 py-1.5 transition-colors disabled:opacity-60"
          data-testid="impersonation-banner-return-btn"
        >
          {busy ? <FreqLoader size={12} className="animate-spin" /> : <ArrowLeft size={12} />}
          Return to admin
        </button>
      </div>
    </div>
  );
}
